/** @jsxImportSource react */
import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from '../components/ui/Toast';
import { ArrowRight, LogIn, Info, Globe, Loader2, Mail, Lock } from 'lucide-react';

import { api } from '../services/api';
import { useTranslation } from '../i18n';

const LoginPage: React.FC = () => {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const { t } = useTranslation();

  const mutation = useMutation({
    mutationFn: () => api.login(email, password),
    onSuccess: () => {
      // AuthProvider will handle user state via onAuthStateChanged
      // App.tsx automatically redirects logged in users to /dashboard
    },
    onError: (error: any) => {
      let message = t('unexpectedError');
      if (error.code) {
          switch (error.code) {
              case 'auth/user-not-found':
              case 'auth/wrong-password':
              case 'auth/invalid-credential':
                  message = t('loginErrorInvalid') || 'Invalid email or password.';
                  break;
              case 'auth/invalid-email':
                  message = t('invalidEmailError') || 'Please enter a valid email address.';
                  break;
              case 'auth/user-disabled':
                  message = t('userDisabledError') || 'This user account has been disabled.';
                  break;
              default:
                  message = error.message || `${t('unexpectedError')} (Code: ${error.code})`;
          }
      } else if (error.message) {
          message = error.message;
      }
      toast.error(message);
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate();
  };

  return (
    <div className="relative min-h-screen w-full bg-white flex items-center justify-center p-4 overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary-500/10 rounded-full mix-blend-multiply filter blur-3xl opacity-50"></div>
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-secondary-500/10 rounded-full mix-blend-multiply filter blur-3xl opacity-50"></div>
      
      <div className="relative z-10 w-full max-w-4xl mx-auto grid md:grid-cols-2 shadow-2xl rounded-xl my-12 border border-secondary-100 bg-white overflow-hidden">
        {/* Left panel - decorative with info */}
        <div className="hidden md:flex flex-col items-center justify-center p-12 text-center relative overflow-hidden bg-secondary-50/50">
          <div className="absolute -top-20 -left-20 w-48 h-48 bg-primary-500/10 rounded-full animate-pulse"></div>
          <div className="absolute bottom-4 -right-12 w-48 h-48 bg-primary-300/10 rounded-full animate-pulse"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-center mb-6">
              <div className="h-16 w-16 bg-primary-500 rounded-lg flex items-center justify-center text-white">
                <LogIn size={32} />
              </div>
            </div>
            <h2 className="text-3xl font-bold mt-4 text-secondary-900">
              {t('welcomeBack')}
            </h2>
            <p className="mt-2 text-secondary-600">
              {t('signInToDashboard')}
            </p>
            <div className="mt-8 text-start">
              <div
                className={`flex items-center space-x-2 mb-4`}
              >
                <div className="h-10 w-10 bg-primary-500/10 rounded-full flex items-center justify-center text-primary-500 flex-shrink-0">
                  <ArrowRight className="rtl:rotate-180" />
                </div>
                <span className="text-secondary-700">
                  {t('fastSecureLogin')}
                </span>
              </div>
              <div
                className={`flex items-center space-x-2 mb-4`}
              >
                <div className="h-10 w-10 bg-primary-500/10 rounded-full flex items-center justify-center text-primary-500 flex-shrink-0">
                  <Info />
                </div>
                <span className="text-secondary-700">
                  {t('support247')}
                </span>
              </div>
              <div
                className={`flex items-center space-x-2`}
              >
                <div className="h-10 w-10 bg-primary-500/10 rounded-full flex items-center justify-center text-primary-500 flex-shrink-0">
                  <Globe />
                </div>
                <span className="text-secondary-700">
                  {t('globalShippingNetwork')}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Right panel - login form */}
        <div className="bg-white p-8 sm:p-12">
          <div className="md:hidden flex justify-center mb-6">
            <div className="h-16 w-16 bg-primary-500 rounded-lg flex items-center justify-center text-white">
              <LogIn size={32} />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-secondary-900 text-center mb-2">
            {t('signIn')}
          </h2>
          <p className="text-center text-secondary-500 mb-8">
            {t('signInToDashboard')}
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                className="block text-secondary-700 text-sm font-medium mb-2"
                htmlFor="email"
              >
                {t('email_label')}
              </label>
              <div className="relative">
                <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                    <Mail size={18} className="text-secondary-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('emailPlaceholder')}
                  required
                  className={`w-full py-3 ps-10 pe-4 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200`}
                />
              </div>
            </div>
            <div>
              <label
                className="block text-secondary-700 text-sm font-medium mb-2"
                htmlFor="password"
              >
                {t('password_label')}
              </label>
               <div className="relative">
                <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                    <Lock size={18} className="text-secondary-400" />
                </div>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('passwordPlaceholder')}
                  required
                  className={`w-full py-3 ps-10 pe-4 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200`}
                />
              </div>
            </div>
            <div
              className={`flex items-center justify-between pt-2`}
            >
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-secondary-300 rounded cursor-pointer"
                />
                <label
                  htmlFor="remember-me"
                  className={`ms-2 block text-sm text-secondary-700 cursor-pointer`}
                >
                  {t('rememberMe')}
                </label>
              </div>
              <div className="text-sm">
                <Link
                  to="/forgot-password"
                  className="font-medium text-primary-500 hover:text-primary-600 transition-colors"
                >
                  {t('forgotPassword')}
                </Link>
              </div>
            </div>
            <button
              type="submit"
              disabled={mutation.isPending}
              className={`w-full flex items-center justify-center bg-primary-500 text-white py-3 px-4 rounded-lg font-medium mt-4 transition-all duration-300 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${mutation.isPending ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {mutation.isPending ? (
                <Loader2 className="animate-spin h-5 w-5 me-2" />
              ) : (
                <>
                  {t('signIn')}
                  <ArrowRight
                    className={`ms-2 h-5 w-5 rtl:rotate-180`}
                  />
                </>
              )}
            </button>
          </form>
          <div className="text-center mt-8">
            <p className="text-sm text-secondary-600">
              {t('dontHaveAccount')}{' '}
              <Link
                to="/signup"
                className="font-medium text-primary-500 hover:text-primary-600 transition-colors"
              >
                {t('createAccount')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
export default LoginPage;